// B.C.'s permanent UTC-7 on devices whose zone data is older than tzdata 2026b (QA-791). Since
// 1 November 2026 British Columbia stays at UTC-7, but a browser or iPad that hasn't shipped 2026b
// still falls back to PST, an hour behind (Chromium 153 formats 17:00Z as 09:00 PST). The web and the
// tech app format every time on the device, so on such an engine every B.C. time they show, and every
// wall time they turn into an instant, would be an hour out.
//
// installZoneFix() checks the engine once and does nothing on one that's current (the server, any
// updated browser). On a stale one it wraps Intl.DateTimeFormat and Date's toLocale*String so that,
// for B.C.'s zone only and only for an instant from the change on, the time is formatted at UTC-7
// (Etc/GMT+7, which every engine has right). Earlier instants keep the engine's own rules, which are
// right for them, and a zone name ("PDT", "Pacific Time") reads as an updated engine shows it.

/** The first instant the old rules get wrong: 2 a.m. PDT on 1 November 2026, when PST would have begun. */
export const BC_FIXED_FROM = Date.parse('2026-11-01T09:00:00Z');

const BC_ZONES: ReadonlySet<string> = new Set(['America/Vancouver', 'Canada/Pacific']);
const FIXED_ZONE = 'Etc/GMT+7';
/** A summer instant: the zone's name there is the one an updated engine shows all year now. */
const NAME_AT = new Date('2026-07-01T19:00:00Z');

type DTFConstructor = typeof Intl.DateTimeFormat;
type DateLike = Date | number | undefined;

const WINTER_NOON = new Date('2026-12-01T12:00:00Z');

function hourAt(DTF: DTFConstructor, timeZone: string): number | undefined {
  try {
    const hour = new DTF('en-US', { timeZone, hour: '2-digit', hourCycle: 'h23' })
      .formatToParts(WINTER_NOON)
      .find((p) => p.type === 'hour')?.value;
    return hour === undefined ? undefined : Number(hour);
  } catch {
    return undefined;
  }
}

/**
 * True when this engine still puts B.C. on PST in winter (1 December 2026, noon UTC, isn't 05:00
 * there) and has UTC-7 itself right, so the fix can mend it.
 */
export function bcZoneIsStale(DTF: DTFConstructor = Intl.DateTimeFormat): boolean {
  const bc = hourAt(DTF, 'America/Vancouver');
  return bc !== undefined && bc !== 5 && hourAt(DTF, FIXED_ZONE) === 5;
}

const timeOf = (d: DateLike): number => (d === undefined ? Date.now() : Number(d));

/**
 * Intl.DateTimeFormat as it should be on a stale engine: a real formatter for every zone but B.C.'s;
 * for B.C.'s, one that picks, per date formatted, the engine's own rules (before the change) or UTC-7
 * (from it), with the zone's name as an updated engine gives it.
 */
export function bcFixedDateTimeFormat(Original: DTFConstructor): DTFConstructor {
  function DateTimeFormat(
    this: unknown,
    locales?: string | string[],
    options?: Intl.DateTimeFormatOptions,
  ): Intl.DateTimeFormat {
    const own = new Original(locales, options);
    const resolved = own.resolvedOptions();
    if (!BC_ZONES.has(resolved.timeZone) && !BC_ZONES.has(options?.timeZone ?? '')) return own;
    const fixed = new Original(locales, { ...options, timeZone: FIXED_ZONE });
    // The zone's name, when the format shows one (timeZoneName, or a long or full timeStyle): the
    // engine's own, as in summer, in place of UTC-7's ("GMT-7"). Strings are renamed in format's own
    // output rather than joined from parts, which can space differently (V8 swaps U+202F only in format).
    const nameIn = (parts: Intl.DateTimeFormatPart[]) => parts.find((p) => p.type === 'timeZoneName')?.value;
    const renaming = (name: string | undefined, fixedName: string | undefined) => {
      const named = name !== undefined && fixedName !== undefined && name !== fixedName;
      return {
        parts: (parts: Intl.DateTimeFormatPart[]) =>
          !named ? parts : parts.map((p) => (p.type === 'timeZoneName' ? { ...p, value: name } : p)),
        text: (text: string) => (!named ? text : text.split(fixedName).join(name)),
      };
    };
    const one = renaming(nameIn(own.formatToParts(NAME_AT)), nameIn(fixed.formatToParts(NAME_AT)));
    // A range can name the zone in another style than a single date does (ICU's "long" range says "PDT").
    let range: ReturnType<typeof renaming> | undefined;
    const ranged = () => {
      const until = NAME_AT.getTime() + 3_600_000;
      return (range ??= renaming(
        nameIn(own.formatRangeToParts(NAME_AT, until)),
        nameIn(fixed.formatRangeToParts(NAME_AT, until)),
      ));
    };
    const late = (d: DateLike) => timeOf(d) >= BC_FIXED_FROM;
    const format = (d?: DateLike) => (late(d) ? one.text(fixed.format(d)) : own.format(d));
    const f = Object.create(Original.prototype) as Intl.DateTimeFormat;
    Object.defineProperties(f, {
      // A getter, as on a real formatter, so `const { format } = f` and `.map(f.format)` work.
      format: { get: () => format },
      formatToParts: {
        value: (d?: DateLike) => (late(d) ? one.parts(fixed.formatToParts(d)) : own.formatToParts(d)),
      },
      // A range is formatted by its start's rules (a range across the change is the one compromise).
      formatRange: {
        value: (a: Date | number, b: Date | number) =>
          late(a) ? ranged().text(fixed.formatRange(a, b)) : own.formatRange(a, b),
      },
      formatRangeToParts: {
        value: (a: Date | number, b: Date | number) =>
          late(a) ? ranged().parts(fixed.formatRangeToParts(a, b)) : own.formatRangeToParts(a, b),
      },
      resolvedOptions: { value: () => own.resolvedOptions() },
    });
    return f;
  }
  DateTimeFormat.prototype = Original.prototype;
  DateTimeFormat.supportedLocalesOf = Original.supportedLocalesOf.bind(Original);
  return DateTimeFormat as unknown as DTFConstructor;
}

const DATE_FIELDS = ['weekday', 'year', 'month', 'day'] as const;
const TIME_FIELDS = ['dayPeriod', 'hour', 'minute', 'second', 'fractionalSecondDigits'] as const;
const NUMERIC_DATE = { year: 'numeric', month: 'numeric', day: 'numeric' } as const;
const NUMERIC_TIME = { hour: 'numeric', minute: 'numeric', second: 'numeric' } as const;

/** Date's toLocale*String defaults (ECMA-402 ToDateTimeOptions), so the formatter says what Date would. */
function withDefaults(
  options: Intl.DateTimeFormatOptions | undefined,
  kind: 'any' | 'date' | 'time',
): Intl.DateTimeFormatOptions {
  const o = { ...options } as Record<string, unknown>;
  const given = (keys: readonly string[]) => keys.some((k) => o[k] !== undefined);
  const needDefaults =
    !(kind !== 'time' && given(DATE_FIELDS)) &&
    !(kind !== 'date' && given(TIME_FIELDS)) &&
    o.dateStyle === undefined &&
    o.timeStyle === undefined;
  if (!needDefaults) return o;
  return { ...o, ...(kind !== 'time' ? NUMERIC_DATE : {}), ...(kind !== 'date' ? NUMERIC_TIME : {}) };
}

interface ZoneFixTarget {
  Intl: { DateTimeFormat: DTFConstructor };
  Date: { prototype: Date };
}

const installed = new WeakSet<object>();

/**
 * Puts the fix in place on a stale engine, once; on a current one it changes nothing and says so.
 * Call it before anything formats a time: the first line of an app's entry.
 */
export function installZoneFix(
  target: ZoneFixTarget = globalThis as unknown as ZoneFixTarget,
  stale: boolean = bcZoneIsStale(target.Intl.DateTimeFormat),
): boolean {
  if (!stale || installed.has(target)) return false;
  installed.add(target);
  const Original = target.Intl.DateTimeFormat;
  const Fixed = bcFixedDateTimeFormat(Original);
  target.Intl.DateTimeFormat = Fixed;
  const proto = target.Date.prototype;
  const ownString = proto.toLocaleString;
  const ownDate = proto.toLocaleDateString;
  const ownTime = proto.toLocaleTimeString;
  const via = (
    own: (this: Date, l?: string | string[], o?: Intl.DateTimeFormatOptions) => string,
    kind: 'any' | 'date' | 'time',
  ) =>
    function (this: Date, locales?: string | string[], options?: Intl.DateTimeFormatOptions): string {
      // Before the change (and an invalid date) the engine's own answer is already right.
      if (!(this.getTime() >= BC_FIXED_FROM)) return own.call(this, locales, options);
      const f = new Fixed(locales, withDefaults(options, kind));
      return BC_ZONES.has(f.resolvedOptions().timeZone) ? f.format(this) : own.call(this, locales, options);
    };
  proto.toLocaleString = via(ownString as never, 'any');
  proto.toLocaleDateString = via(ownDate as never, 'date');
  proto.toLocaleTimeString = via(ownTime as never, 'time');
  return true;
}
